import React, { useState } from 'react';
import './Sidebar.css';

const Sidebar = ({ activeMenu, setActiveMenu, userRole, isMobileOpen, onMobileClose }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});

  const allMenuItems = [
    { id: 'dashboard', icon: '📊', label: 'Dashboard', path: '/dashboard', roles: [1] },
    { 
      id: 'policies', 
      icon: '📋', 
      label: 'Policies', 
      roles: [1, 2],
      subItems: [
        { id: 'new-policies', label: 'New Policies', roles: [1, 2] },
        { id: 'pending-approval', label: 'Pending Approval', roles: [1, 2] },
        { id: 'approved-policies', label: 'Approved Policies', roles: [1, 2] },
        { id: 'expired-policies', label: 'Expired Policies', roles: [1, 2] }
      ]
    },
    { 
      id: 'declarations', 
      icon: '📋', 
      label: 'Declarations', 
      roles: [1, 2],
      subItems: [
        { id: 'new-declarations', label: 'New Declarations', roles: [1, 2] },
        { id: 'posted-declarations', label: 'Posted Declarations', roles: [1, 2] }
      ]
    },
    { id: 'products', icon: '📦', label: 'Products', path: '/products', roles: [1] },
    { id: 'underwriting-companies', icon: '🏢', label: 'Underwriting Companies', path: '/underwriting-companies', roles: [1] },
    {
      id: 'cancellations',
      icon: '❌', 
      label: 'Cancellations', 
      roles: [1, 2],
      subItems: [
        { id: 'pending-cancellations', label: 'Pending Approval', roles: [1, 2] },
        { id: 'approved-cancellations', label: 'Approved', roles: [1, 2] }
      ]
    },
    { 
      id: 'extensions', 
      icon: '🔄', 
      label: 'Extensions', 
      roles: [1, 2],
      subItems: [
        { id: 'new-extensions', label: 'New Extensions', roles: [1, 2] },
        { id: 'pending-extensions', label: 'Pending Approval', roles: [1, 2] },
        { id: 'approved-extensions', label: 'Approved', roles: [1, 2] }
      ]
    },
    { id: 'clients', icon: '👥', label: 'Clients', path: '/clients', roles: [1] },
    { id: 'admins', icon: '👤', label: 'Users', path: '/admins', roles: [1] },
    { id: 'pending-premium-submissions', icon: '🕒', label: 'Declarations Due', path: '/pending-premium-submissions', roles: [1] },
    { id: 'reports', icon: '📈', label: 'Reports', path: '/reports', roles: [1] },
    { id: 'profile', icon: '⚙️', label: 'My Profile', path: '/profile', roles: [1, 2] }
  ];

  // Filter menu items based on user role
  const menuItems = allMenuItems.filter(item => item.roles.includes(userRole));

  // Auto-expand menu if a submenu item is active
  React.useEffect(() => {
    menuItems.forEach(item => {
      if (item.subItems) {
        const hasActiveSubItem = item.subItems.some(subItem => subItem.id === activeMenu);
        if (hasActiveSubItem && !expandedMenus[item.id]) {
          setExpandedMenus(prev => ({ ...prev, [item.id]: true }));
        }
      }
    });
  }, [activeMenu]);

  const handleMenuClick = (item) => {
    if (item.subItems) {
      setExpandedMenus({
        ...expandedMenus,
        [item.id]: !expandedMenus[item.id]
      });
    } else {
      setActiveMenu(item.id);
      // Close mobile menu when item is clicked
      if (onMobileClose) {
        onMobileClose();
      }
    }
  };

  // Check if any subitem of a menu is active
  const isMenuActive = (item) => {
    if (!item.subItems) return activeMenu === item.id;
    return item.subItems.some(subItem => subItem.id === activeMenu);
  };

  return (
    <>
      {/* Mobile overlay */}
      {isMobileOpen && (
        <div className="mobile-overlay active" onClick={onMobileClose}></div>
      )}
      
      <div className={`sidebar ${isCollapsed ? 'collapsed' : ''} ${isMobileOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <span className="logo-icon">🛡️</span>
            {!isCollapsed && <span className="logo-text">Zakim</span>}
          </div>
          <button 
            className="collapse-btn"
            onClick={() => setIsCollapsed(!isCollapsed)}
            title={isCollapsed ? 'Expand' : 'Collapse'}
          >
            {isCollapsed ? '»' : '«'}
          </button>
        </div>

        <nav className="sidebar-menu">
          {menuItems.map((item) => (
            <div key={item.id} className="menu-item-container">
              <div
                className={`menu-item ${isMenuActive(item) ? 'active' : ''}`}
                onClick={() => handleMenuClick(item)}
              >
                <span className="menu-icon">{item.icon}</span>
                {!isCollapsed && <span className="menu-label">{item.label}</span>}
                {!isCollapsed && item.subItems && (
                  <span className="submenu-arrow">
                    {expandedMenus[item.id] ? '▼' : '▶'}
                  </span>
                )}
                {!isCollapsed && activeMenu === item.id && !item.subItems && (
                  <span className="active-indicator"></span>
                )}
              </div>
              
              {/* Sub-menu items */}
              {!isCollapsed && item.subItems && expandedMenus[item.id] && (
                <div className="submenu">
                  {item.subItems
                    .filter(subItem => subItem.roles.includes(userRole))
                    .map(subItem => (
                      <div
                        key={subItem.id}
                        className={`submenu-item ${activeMenu === subItem.id ? 'active' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveMenu(subItem.id);
                          // Close mobile menu when sub-item is clicked
                          if (onMobileClose) {
                            onMobileClose();
                          }
                        }}
                      >
                        <span className="submenu-label">{subItem.label}</span>
                        {activeMenu === subItem.id && (
                          <span className="active-indicator"></span>
                        )}
                      </div>
                    ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="user-profile">
            <div className="user-avatar">👤</div>
            {!isCollapsed && (
              <div className="user-info">
                <div className="user-name">{localStorage.getItem('username') || 'User'}</div>
                <div className="user-role">{userRole === 1 ? 'Administrator' : 'Policy Manager'}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
